import React from 'react';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import { useAuditLog } from '../services/api';
import useMockDataToast from '../hooks/useMockDataToast';

const columnLabels = ['Timestamp', 'Actor', 'Role', 'Action', 'Resource', 'Detail'] as const;

const AuditLog: React.FC = () => {
  const {
    data: entries = [],
    isLoading,
    error,
    isFallback,
    fallbackMessage,
  } = useAuditLog(true);
  useMockDataToast(isFallback, 'Audit service unavailable. Showing cached timeline.', fallbackMessage);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="section-title">Regulatory trail</p>
          <h1 className="text-2xl font-semibold text-slate-900">Audit log</h1>
          <p className="text-sm text-slate-500">
            Every sensitive action is recorded for hospital compliance and client trust.
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
          <div className="flex items-center gap-2 font-semibold text-slate-900">
            <ShieldCheckIcon className="h-5 w-5 text-brand-blue" />
            Tamper evident
          </div>
          <p className="text-xs text-slate-500">Mirrored to Cloudflare D1 + Logpush</p>
        </div>
      </div>

      {error && (
        <div className="glass-panel border border-rose-200 bg-rose-50 p-6 text-rose-700">
          <p className="text-sm font-semibold">Unable to load audit trail.</p>
          <p className="text-xs mt-1">
            {error instanceof Error ? error.message : 'Please try again in a few minutes.'}
          </p>
        </div>
      )}

      <section className="glass-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] table-auto divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                {columnLabels.map((label) => (
                  <th key={label} className="px-5 py-3">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {isLoading ? (
                <tr>
                  <td colSpan={columnLabels.length} className="px-5 py-10 text-center text-slate-400">
                    Syncing audit entries…
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-slate-50">
                    <td className="px-5 py-3 text-xs text-slate-500">
                      {new Date(entry.timestamp).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 font-semibold text-slate-900">{entry.actor}</td>
                    <td className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {entry.role}
                    </td>
                    <td className="px-5 py-3 text-slate-700">{entry.action}</td>
                    <td className="px-5 py-3 text-slate-500">{entry.resource}</td>
                    <td className="px-5 py-3 text-slate-700">{entry.detail ?? '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default AuditLog;
